/**
 * @file Lists a project's investor batches and the files inside them, for use as transfer sources.
 */
import { indexProjectBatchesOperation, indexBatchFilesOperation } from "@api/services/backstage";

import type { Batch, Request, InvestorFile } from "../types";

type BatchesOutput = {
  data: Batch[];
};

type FilesOutput = {
  data: InvestorFile[];
};

/**
 * Fetches every batch of the given project.
 */
export const listProjectBatches = async (request: Request, projectId: string): Promise<Batch[]> => {
  const { data: projectBatches } = await request<BatchesOutput>(indexProjectBatchesOperation, { projectId });

  return projectBatches;
};

/**
 * Fetches the project's investor batches and every file inside them.
 */
const listProjectFiles = async (
  request: Request,
  projectId: string,
): Promise<[Batch[], InvestorFile[]]> => {
  const files: InvestorFile[] = [];

  const projectBatches = await listProjectBatches(request, projectId);

  // NOTE: Include only investor files and not project files.
  const investorBatches = projectBatches
    .filter((batch) => !batch.fileUrl);

  const promises = investorBatches
    .map(({ id: batchId }) =>
      request<FilesOutput>(indexBatchFilesOperation, { batchId })
        .then(({ data: batchFiles }) => files.push(...batchFiles)),
    );

  await Promise.all(promises);

  return [ investorBatches, files ];
};

export default listProjectFiles;
